import { createFileRoute } from '@tanstack/react-router'

import { ArrivalCard, DeliveredCard, HandoverCard } from '@/components/fotograf/foceni'
import { JellyIcon } from '@/components/icons'
import { BriefList, SectionLabel, StatusBadge, UrgentPanel } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import {
  ARRIVAL,
  CONSENT,
  DELIVERED,
  HANDOVER,
  SHOOT,
  SHOOT_BRIEF,
  SHOT_LIST_AFTER,
  UPLOAD_DUE,
} from '@/mocks/fotograf/foceni'

/**
 * The shoot's three moments, all linkable: before it, the 48 hours after it
 * (spec §7.4), and once it is handed over.
 */
type ShootSearch = { stav?: 'odevzdani' | 'odevzdano' }

export const Route = createFileRoute('/fotograf/foceni/$shoot')({
  validateSearch: (search: Record<string, unknown>): ShootSearch =>
    search.stav === 'odevzdani' || search.stav === 'odevzdano' ? { stav: search.stav } : {},
  head: () => ({ meta: [{ title: 'Focení · Aplikace Bitez' }] }),
  component: FotografShoot,
})

/**
 * The photographer's shoot — the screen his whole day hangs on, and the place
 * where everything the app collected finally meets: the client's booking, the
 * arrival notes they wrote in onboarding step 4, the shot list they and Bára
 * wrote together, and the 48h clock that starts the moment he leaves.
 *
 * One screen, three moments, because it is one job: **before** it is about
 * getting in and knowing what to shoot; **after** it is about handing over,
 * which is the only part that can cost him money (spec §10). The arrival notes
 * and the date disappear once he has been there — he is not standing in front
 * of the door any more.
 */
function FotografShoot() {
  const { stav } = Route.useSearch()
  const handing = stav === 'odevzdani'
  const delivered = stav === 'odevzdano'
  const shot = handing || delivered

  return (
    <div className="px-4 pt-4 pb-6">
      <Link
        href="/fotograf/prehled"
        className="-ml-1 flex w-fit items-center gap-1.5 rounded-full px-1 py-1 text-[10px] font-bold tracking-widest text-ink/40 uppercase transition-colors duration-150 hover:text-ink/70"
      >
        <JellyIcon name="arrow-left" className="size-3" />
        Přehled
      </Link>

      <header className="mt-3 flex items-start gap-3.5">
        <img
          src={SHOOT.avatar}
          alt=""
          width={200}
          height={200}
          className="size-14 shrink-0 rounded-2xl object-cover"
        />
        <div className="min-w-0 flex-1">
          <h1 className="font-display truncate text-2xl leading-[0.9] uppercase">{SHOOT.venue}</h1>
          {/* What a photographer actually needs at a glance: how far, and what
              it pays. The package is the client's tier and means nothing to him
              — the fee is the same fact in his language. */}
          <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold text-ink/50">
            <span className="flex items-center gap-1.5">
              <JellyIcon name="car" className="size-3 text-ink/40" />
              {SHOOT.distance}
            </span>
            <span className="flex items-center gap-1.5">
              <JellyIcon name="money-bill" className="size-3 text-ink/40" />
              {SHOOT.fee}
            </span>
          </p>
        </div>
        <StatusBadge
          label={delivered ? 'Odevzdáno' : handing ? 'Odfoceno' : 'Potvrzeno'}
          icon={handing ? 'camera' : 'check'}
          tone={handing ? 'sun' : 'success'}
          variant="soft"
        />
      </header>

      {handing && (
        <>
          {/* His one urgent thing, in the app's one urgent shape (spec §7.4) */}
          <div className="animate-rise-up mt-5">
            <UrgentPanel
              figure={UPLOAD_DUE.hoursLeft}
              icon="arrow-up-from-line"
              title={
                <>
                  na odevzdání
                  <br />
                  podkladů
                </>
              }
              note={<>Focení {UPLOAD_DUE.shotAt}</>}
            />
          </div>

          {/* No section label: the panel above already says what this is, and
              the card names itself. */}
          <div className="animate-rise-up mt-4" style={{ animationDelay: '60ms' }}>
            <HandoverCard handover={HANDOVER} />
          </div>
        </>
      )}

      {/* No rating here: it lives in the overlay on his overview, where it is
          asked once and follows him until he answers (owner, 2026-08-19). */}
      {delivered && (
        <div className="animate-rise-up mt-5">
          <DeliveredCard delivered={DELIVERED} />
        </div>
      )}

      {/* Before the shoot: when it is and how to get in. Afterwards both are
          gone — he has already been there, and the screen is now about the
          handover, not about a door he is standing at. */}
      {!shot && (
        <>
          <div className="animate-rise-up mt-5 flex items-center gap-3.5 rounded-3xl bg-white p-5 shadow-sm">
            <span className="flex size-14 shrink-0 flex-col items-center justify-center rounded-2xl bg-surface">
              <span className="font-display text-xl leading-none">{SHOOT.dayNumber}</span>
              <span className="mt-0.5 text-[9px] font-bold tracking-wider text-ink/45 uppercase">
                {SHOOT.month}
              </span>
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-display text-lg leading-none uppercase">{SHOOT.date}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1.5 rounded-full bg-brand px-2.5 py-1 text-[11px] font-bold text-white">
                  <JellyIcon name="clock" className="size-3" />
                  {SHOOT.time}
                </span>
                <span className="text-[11px] font-semibold text-ink/50">
                  Počítejte s {SHOOT.duration}
                </span>
              </div>
            </div>
          </div>

          <section className="animate-rise-up mt-8" style={{ animationDelay: '60ms' }}>
            <SectionLabel>Kam přijet</SectionLabel>
            <div className="mt-3">
              <ArrivalCard arrival={ARRIVAL} />
            </div>
          </section>
        </>
      )}

      {/* The same list all along: a plan before the shoot, a check against it
          after — which is the moment the ticking finally pays for itself. */}
      <section className="animate-rise-up mt-8" style={{ animationDelay: '120ms' }}>
        {/* No explaining line: the faces under each item already say who asked
            for it, and a circle you can tick does not need instructions. */}
        <SectionLabel>{shot ? 'Co jste měl nafotit' : 'Co nafotit'}</SectionLabel>
        <div className="mt-4">
          <BriefList groups={shot ? SHOT_LIST_AFTER : SHOOT_BRIEF} checkable />
        </div>
      </section>

      {/* The question always comes up on a shoot, and it always goes to her
          (B12) — so the way to ask sits on the shoot, not in a tab. */}
      <div className="animate-rise-up mt-8" style={{ animationDelay: '150ms' }}>
        <Link
          href="/fotograf/chat"
          className="group flex items-center gap-3 rounded-3xl bg-white p-4 shadow-sm transition-[translate,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-md"
        >
          <span
            aria-hidden
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-sun/60 text-ink"
          >
            <JellyIcon name="comment" className="size-3.5" />
          </span>
          <span className="min-w-0 flex-1 text-xs font-bold">Zeptat se Báry</span>
          <JellyIcon
            name="arrow-right"
            className="size-3.5 shrink-0 text-ink/40 transition-transform duration-200 group-hover:translate-x-0.5"
          />
        </Link>
      </div>

      {/* His to respect, and the one thing he cannot ask about on the spot
          without making it awkward (onboarding step 4). */}
      {!shot && (
        <section className="animate-rise-up mt-8" style={{ animationDelay: '180ms' }}>
          <SectionLabel>Focení lidí</SectionLabel>
          <div className="mt-3 space-y-3 rounded-3xl bg-white p-5 shadow-sm">
            {CONSENT.map((rule) => (
              <div key={rule.text} className="flex items-start gap-3">
                <span
                  aria-hidden
                  className={`flex size-7 shrink-0 items-center justify-center rounded-full ${
                    rule.allowed ? 'bg-success/10 text-success' : 'bg-surface text-ink/50'
                  }`}
                >
                  <JellyIcon
                    name={rule.allowed ? 'check' : 'lock'}
                    variant="fill"
                    className="size-3"
                  />
                </span>
                <p className="pt-1 text-xs leading-relaxed text-ink/70">{rule.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
